import * as ko from "knockout";
import {IStatusDataParams, TSyncStatus} from "./index";
import {MutableImportFilterData} from "./cards/importfilter/MutableImportFilterData";
import {MutableCompany} from "./MutableCompany";
import {Permission} from "../../../constants/Permission";
import {MutableNotificationData} from "./cards/notification/MutableNotificationData";
import {global} from "../../../appContext";
import {MutableSyncCycle} from "./synccycle/MutableSyncCycle";
import {MutableCompanySyncStep} from "./synccycle/MutableCompanySyncStep";
import {
    ICompany,
    ICompanySyncstep,
    ICompanySyncstepFilter,
    ICompanySyncstepParameter,
    IFormTemplate,
    IImportHrFile,
    IMessageData,
    ISyncExportQueueItem,
    ISyncstep
} from "../../../";
import {_} from 'lodash';
import PromiseRequest from "../../../req/PromiseRequest";
import {nsCustomerGeneral} from "../../customer/general/fn";
import {nsUtils} from "./nsUtils";
import {PLEASE_WAIT_MESSAGE} from "../../../constants/Language";
import {_d, createErrorMessage, decodeAndStripHtml} from "../../../util/misc";
import {TServiceName} from "../../../constants/ApiServices";
import {CHECK_FOR_SYNC_UPDATES_THROTTLE_DURATION} from "../../../constants";
import {nsSync} from "../../../util/nsSync";
import urlEncodeCustomerKeys = nsCustomerGeneral.urlEncodeCustomerKeys;

enum StatusIcons {
    'halted' = 'fa-exclamation-circle',
    'waiting' = 'fa-clock',
    'executed' = 'fa-check-circle',
    'executing' = 'fa-sync fa-spin',
    'unknown' = 'fa-question-circle',
    'executedwitherrors' = 'fa-check-circle',
}

export class StatusManager implements IStatusDataParams {

    public readonly isSubmitting = ko.observable(false)
    public readonly notifyAboutPauseDeltaSync = ko.observable(false)
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null)
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);

    public mutableCompany!: MutableCompany
    public mutableImportFilter!: MutableImportFilterData
    public mutableNotification!: MutableNotificationData
    public mutableSyncCycle!: MutableSyncCycle

    public readonly user = global.login.user()
    public readonly permission: KnockoutObservable<Permission>

    public company!: ICompany
    public importfile!: IImportHrFile
    public syncsteps!: ISyncstep[]
    public syncexportqueue!: ISyncExportQueueItem[]

    public readonly req_addsyncstep = new PromiseRequest('addsyncstep')
    public readonly req_createcompanysyncstep = new PromiseRequest('createcompanysyncstep')
    public readonly req_createcompanysyncstepfilter = new PromiseRequest('createcompanysyncstepfilter')
    public readonly req_createcompanysyncstepparameter = new PromiseRequest('createcompanysyncstepparameter')
    public readonly req_deletecompanysyncstep = new PromiseRequest('deletecompanysyncstep')
    public readonly req_deletecompanysyncstepfilter = new PromiseRequest('deletecompanysyncstepfilter')
    public readonly req_deletecompanysyncstepparameter = new PromiseRequest('deletecompanysyncstepparameter')
    public readonly req_deletelastsynccycle = new PromiseRequest('deletelastsynccycle')
    public readonly req_deletelastsyncstep = new PromiseRequest('deletelastsyncstep')
    public readonly req_emptycache = new PromiseRequest('emptycache')
    public readonly req_getcompany = new PromiseRequest('getcompany')
    public readonly req_getimporthrfile = new PromiseRequest('getimporthrfile')
    public readonly req_getsyncexportqueue = new PromiseRequest('getsyncexportqueue')
    public readonly req_getsyncsteps = new PromiseRequest('getsyncsteps')
    public readonly req_resumesynccycle = new PromiseRequest('resumesynccycle')
    public readonly req_schedulesync = new PromiseRequest('schedulesync')
    public readonly req_updatecompany = new PromiseRequest('updatecompany')
    public readonly req_updatecompanysyncstep = new PromiseRequest('updatecompanysyncstep')
    public readonly req_updatecompanysyncstepfilter = new PromiseRequest('updatecompanysyncstepfilter')
    public readonly req_updatecompanysyncstepparameter = new PromiseRequest('updatecompanysyncstepparameter')
    public hasImportfile!: boolean

    private checkForSyncUpdate_timeoutId!: number;
    public readonly isSyncUpdated: KnockoutObservable<boolean> = ko.observable(false)

    constructor(params: IStatusDataParams) {
        this.permission = params.permission
    }

    public init() {
        return Promise.resolve()
            .then(() => {
                this.stopCheckForSyncUpdates()

                this.isSubmitting(true)
                this.isReady(false)
                this.messageData({message: PLEASE_WAIT_MESSAGE})
            })
            .then(() => this.do_getsyncsteps())
            .then(() => this.do_getcompany())
            .then(() => this.do_getimporthrfile())
            .then(() => this.do_getsyncexportqueue())
            .then(() => {
                this.mutableCompany = this.mutableCompany ?? new MutableCompany(this)
                this.mutableImportFilter = this.mutableImportFilter ?? new MutableImportFilterData(this)
                this.mutableNotification = this.mutableNotification ?? new MutableNotificationData(this)
                this.mutableSyncCycle = this.mutableSyncCycle ?? new MutableSyncCycle(this)

                this.mutableCompany.init()
                this.mutableImportFilter.init()
                this.mutableNotification.init()
                this.mutableSyncCycle.init()

                this.startCheckForSyncUpdates()
            })
            .then(() => {
                this.messageData(null)
                this.isReady(true)
                this.isSubmitting(false)
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
            })
    }

    public getStatusIcon(status: TSyncStatus) {
        return StatusIcons[status]
    }

    public getSyncCycleStatus(): TSyncStatus {
        const importfileSyncstep = _(this.importfile?.synccyclesteps)?.last()

        if (!importfileSyncstep) {
            return 'waiting'
        }

        if (importfileSyncstep.halted) {
            return 'halted';
        }

        const companySyncStep = _.last(this.getSortedCompanySyncsteps())

        if (!companySyncStep) {
            return 'unknown'
        }

        const importFile = this.company.lastsasimportfile;
        const isSyncCycleCompleted = importFile != null
            && (!nsUtils.isNullOrUndefined(importFile.completed) || !!importFile.completed)

        if (isSyncCycleCompleted) {
            const numObjectsWithErrors = this.mutableSyncCycle.mutableCompanySyncsteps()
                .reduce((acc: number, obj: MutableCompanySyncStep) => {
                    return acc + obj.getTranslationObj().numObjects
                }, 0)
            if (numObjectsWithErrors) {
                return 'executedwitherrors'
            }
            return 'executed';
        }

        return 'executing';
    }

    public do_getcompany() {
        this.stopCheckForSyncUpdates()
        this.req_getcompany.abort()
        return this.req_getcompany
            .request()
            .then((data: [ICompany]) => this.company = data[0])
            .then(() => this.hasImportfile = !!this.company.lastsasimportfile?.id)
            .then(() => this.company)
    }

    public do_getimporthrfile(importfileid: number | undefined = this.company.lastsasimportfile?.id) {
        this.req_getimporthrfile.abort()
        return Promise.resolve()
            .then(() => {
                if (importfileid) {
                    return this.req_getimporthrfile.request({importfileid: importfileid})
                } else {
                    return null
                }
            })
            .then((data: [IImportHrFile]) => this.importfile = data?.[0] || {
                completed: null,
                deletedaccounts: null,
                deleteddepartments: null,
                deletedgroups: null,
                editedaccounts: null,
                editeddepartments: null,
                editedgroups: null,
                ended: null,
                errordeletedaccounts: null,
                errordeleteddepartments: null,
                errordeletedgroups: null,
                erroreditedaccounts: null,
                errorediteddepartments: null,
                erroreditedgroups: null,
                errornewaccounts: null,
                errornewdepartments: null,
                errornewgroups: null,
                errorrecoveredaccounts: null,
                id: null,
                newaccounts: null,
                newdepartments: null,
                newgroups: null,
                objecthistories: [],
                pathhifile: null,
                pathhrfile: null,
                pathlogfile: null,
                pathsamaccountfile: null,
                recoveredaccounts: null,
                sequencenr: null,
                started: null,
                synccyclesteps: [],
                syncimportlog: [],
            })
    }

    public do_getsyncsteps() {
        this.req_getsyncsteps.abort()
        return this.req_getsyncsteps
            .request()
            .then((data: ISyncstep[]) => this.syncsteps = data)
    }

    public do_getsyncexportqueue() {
        this.req_getsyncexportqueue.abort()
        return this.req_getsyncexportqueue
            .request()
            .then((data: ISyncExportQueueItem[]) => this.syncexportqueue = data)
    }

    public do_updatecompany(company: Partial<ICompany>) {
        const _company = _.cloneDeep(_.defaultsDeep(company, this.company));
        _company.syncsteps = urlEncodeCustomerKeys(_company.syncsteps)
        const jcompany = JSON.stringify(_company)
        this.req_updatecompany.abort()
        return this.req_updatecompany
            .request({jcompany: jcompany})
            .then(() => this.removeCache())
    }

    public do_deletelastsynccycle(importfileid: number = this.company.lastsyncstep!.importfileid) {
        this.req_deletelastsynccycle.abort()
        return this.req_deletelastsynccycle
            .request({id: importfileid})
            .then(() => this.removeCache())
    }

    public do_addsyncstep(id: number = this.company.lastsyncstep!.importfileid) {
        const companySyncstep = this.getNextCompanySyncstep()
        if (!companySyncstep) {
            return Promise.resolve(null);
        }
        this.req_addsyncstep.abort()
        return this.req_addsyncstep
            .request({id: companySyncstep.syncstep.id})
            .then(() => this.removeCache())
    }

    public do_deletelastsyncstep(id: number = this.company.lastsyncstep!.importfileid) {
        this.req_deletelastsyncstep.abort()
        return this.req_deletelastsyncstep
            .request({id})
            .then(() => this.removeCache())
    }

    public do_resumesynccycle(id: IImportHrFile['id'] = this.importfile.id) {
        this.req_resumesynccycle.abort()
        return this.req_resumesynccycle
            .request({id})
            .then(() => this.removeCache())
    }

    public do_createcompanysyncstep(companySyncstep: ICompanySyncstep) {
        const jcompanysyncstep = JSON.stringify(this.prepareCompanySyncstepForApi(companySyncstep))
        this.req_createcompanysyncstep.abort()
        return this.req_createcompanysyncstep
            .request({companysyncstep: jcompanysyncstep})
    }

    public do_updatecompanysyncstep(companySyncstep: ICompanySyncstep) {
        const jcompanysyncstep = JSON.stringify(this.prepareCompanySyncstepForApi(companySyncstep))
        this.req_updatecompanysyncstep.abort()
        return this.req_updatecompanysyncstep
            .request({companysyncstep: jcompanysyncstep})
    }

    private prepareCompanySyncstepForApi(companySyncstep: ICompanySyncstep) {
        const _companySyncstep = _.cloneDeep(companySyncstep);
        _companySyncstep.eHubForm = (_companySyncstep.eHubForm ?? []).map((o: IFormTemplate) => {
            o.pages = []
            return o
        })
        return _companySyncstep
    }

    public do_deletecompanysyncstep(companySyncstep: ICompanySyncstep) {
        const jcompanysyncstep = JSON.stringify(companySyncstep)
        this.req_deletecompanysyncstep.abort()
        return this.req_deletecompanysyncstep
            .request({companysyncstep: jcompanysyncstep})
    }

    public do_createcompanysyncstepfilter(companysyncstepfilter: ICompanySyncstepFilter) {
        const jcompanysyncstepfilter = JSON.stringify(companysyncstepfilter);
        return this.req_createcompanysyncstepfilter
            .request({companysyncstepfilter: jcompanysyncstepfilter})
            .then(() => this.removeCache())
    }

    public do_deletecompanysyncstepfilter(companysyncstepfilterid: ICompanySyncstepFilter['id']) {
        return this.req_deletecompanysyncstepfilter
            .request({companysyncstepfilterid: companysyncstepfilterid})
            .then(() => this.removeCache())
    }

    public do_updatecompanysyncstepfilter(companysyncstepfilter: ICompanySyncstepFilter) {
        const jcompanysyncstepfilter = JSON.stringify(companysyncstepfilter);
        return this.req_updatecompanysyncstepfilter
            .request({companysyncstepfilter: jcompanysyncstepfilter})
            .then(() => this.removeCache())
    }

    public do_createcompanysyncstepparameter(companysyncstepparameter: ICompanySyncstepParameter) {
        const jcompanysyncstepparameter = JSON.stringify(companysyncstepparameter)
        return this.req_createcompanysyncstepparameter
            .request({companysyncstepparameter: jcompanysyncstepparameter})
    }

    public do_deletecompanysyncstepparameter(companysyncstepparameterid: ICompanySyncstepParameter['id']) {
        return this.req_deletecompanysyncstepparameter
            .request({companysyncstepparameterid: companysyncstepparameterid})
    }

    public do_updatecompanysyncstepparameter(companysyncstepparameter: ICompanySyncstepParameter) {
        const jcompanysyncstepparameter = JSON.stringify(companysyncstepparameter)
        return this.req_updatecompanysyncstepparameter
            .request({companysyncstepparameter: jcompanysyncstepparameter})
    }

    public do_schedulesync(companySyncstepId: ICompanySyncstep['id']) {
        return this.req_schedulesync.request({csseid: companySyncstepId})
    }

    public do_emptycache() {
        return this.req_emptycache.request()
    }

    public removeCache(...serviceNames: TServiceName[]) {
        if (!serviceNames?.length) {
            serviceNames = [
                'getcompany',
                'geteadmstatus',
                'getimporthrfile',
                'getcompanysyncsteps',
                'getallcompanysyncstepparameters',
            ]
        }
        serviceNames.forEach(s => global.cacheManager.removeCache(s))
    }

    public getSortedCompanySyncsteps(): ICompanySyncstep[] {
        return _.sortBy(this.company.syncsteps, 'order')
    }

    public getNextCompanySyncstep(): ICompanySyncstep | null {
        const companySyncsteps = this.getSortedCompanySyncsteps();
        const companySyncstepName = this.company.lastsyncstep?.syncstepname;
        const index = companySyncsteps.findIndex((s: ICompanySyncstep) => s.syncstep.name == companySyncstepName);
        return companySyncsteps[index + 1] ?? null;
    }

    public getNextCompanySyncstepForExecution(): ICompanySyncstep | undefined {
        const companySyncsteps = this.getSortedCompanySyncsteps()
        const lastSyncstepIndex = companySyncsteps.findIndex((o: ICompanySyncstep) => o.syncstep.name == this.company.lastsyncstep?.syncstepname)
        const reorderedSteps = [...companySyncsteps.slice(lastSyncstepIndex + 1), ...companySyncsteps.slice(0, lastSyncstepIndex)]
        return reorderedSteps.find((o: ICompanySyncstep) => {
            return !['Import AD Results', 'ImportHrFuture'].includes(o.syncstep.name)
        })
    }

    public canNextCompanySyncstepBeExecuted(): boolean {
        if (['executed', 'executedwitherrors'].includes(this.getSyncCycleStatus())) {
            return false
        }
        if (!(this.syncexportqueue?.[0]?.exported ?? true)) {
            return false
        }
        const a = this.company.lastsyncstep?.halted ?? false
        const b = this.company.pausedeltasync
        if (a || b) {
            return false
        }
        const nextCompanySyncstepForExecution = this.getNextCompanySyncstepForExecution();

        return !!nextCompanySyncstepForExecution
    }

    public getTranslationObj() {
        const historiesWithErrors = this.importfile?.objecthistories.filter((o) => !!o.lasterror) ?? []
        const numErrors = historiesWithErrors.length
        const numObjects = Object.keys(_.groupBy(historiesWithErrors, (o: IImportHrFile['objecthistories'][number]) => o.userid)).length
        const lastSyncstep = this.company.lastsyncstep;
        let syncstep: ISyncstep | null = null, error: string | null = null, syncstepname: string | null = null
        if (lastSyncstep) {
            syncstep = this.syncsteps.find((o) => o.name == lastSyncstep.syncstepname) ?? null
            error = decodeAndStripHtml(lastSyncstep.result)
            syncstepname = syncstep ? nsSync.getSyncstepName(syncstep) : null
        }
        return {
            numErrors,
            numObjects,
            error,
            syncstepname,
            importfile: this.importfile,
            importfileid: this.importfile?.id,
            status: this.getSyncCycleStatus(),
        }
    }

    public startCheckForSyncUpdates() {
        this.stopCheckForSyncUpdates()

        this.checkForSyncUpdate_timeoutId = setTimeout(() => {
            if (this.isSubmitting()) {
                _d.log(`Aborting status update. Submit in progress...`)
                this.startCheckForSyncUpdates()
                return
            }
            const jOld = JSON.stringify(this.company.lastsyncstep)
            Promise.resolve()
                .then(() => this.removeCache('getcompany'))
                .then(() => this.do_getcompany())
                .then(() => {
                    const jNew = JSON.stringify(this.company.lastsyncstep)
                    const changed = jOld != jNew;
                    if (changed) {
                        this.removeCache('getimporthrfile')
                        return Promise.resolve()
                            .then(() => this.do_getimporthrfile())
                            .then(() => {
                                // trigger subscribers to update
                                this.isSyncUpdated(true)
                                this.isSyncUpdated(false)
                                this.startCheckForSyncUpdates()
                            })
                    } else {
                        this.startCheckForSyncUpdates()
                    }
                })
        }, CHECK_FOR_SYNC_UPDATES_THROTTLE_DURATION)
        _d.log(`Scheduling status update in ${CHECK_FOR_SYNC_UPDATES_THROTTLE_DURATION / 1000} seconds...`)
    }

    public stopCheckForSyncUpdates() {
        clearTimeout(this.checkForSyncUpdate_timeoutId)
    }

    dispose() {
        this.req_addsyncstep.abort()
        this.req_createcompanysyncstep.abort()
        this.req_createcompanysyncstepfilter.abort()
        this.req_createcompanysyncstepparameter.abort()
        this.req_deletecompanysyncstep.abort()
        this.req_deletecompanysyncstepfilter.abort()
        this.req_deletecompanysyncstepparameter.abort()
        this.req_deletelastsynccycle.abort()
        this.req_deletelastsyncstep.abort()
        this.req_emptycache.abort()
        this.req_getcompany.abort()
        this.req_getimporthrfile.abort()
        this.req_getsyncexportqueue.abort()
        this.req_getsyncsteps.abort()
        this.req_resumesynccycle.abort()
        this.req_schedulesync.abort()
        this.req_updatecompany.abort()
        this.req_updatecompanysyncstep.abort()
        this.req_updatecompanysyncstepfilter.abort()
        this.req_updatecompanysyncstepparameter.abort()

        this.mutableImportFilter?.dispose()
        this.mutableNotification?.dispose()
        this.mutableSyncCycle?.dispose()

        this.stopCheckForSyncUpdates()
    }

}